import React, { useEffect, useMemo, useRef, useState } from 'react';

const DEMO_W = 1920;
const DEMO_H = 1080;

export interface KbClip {
  term: string;
  // 0-indexed occurrence of `term` (case-insensitive) the picker chose.
  // Runtime wraps only this Nth match in document order — every other
  // occurrence stays plain. Lets the same word appear unrelated elsewhere.
  termOccurrence?: number;
  // Legacy anchor-based clips (no longer written, kept for old data):
  anchorId?: string;
  iframeUrl: string;
  box: [number, number, number, number];
  actions?: any[];
  durationMs?: number;
  // Pre-rendered demo screenshots. When present the popover renders these
  // instead of loading the live demo iframe — avoids network + render cost
  // on the article page.
  snapshots?: string[];
  // Simplest path — author uploaded a GIF/MP4/WebM (or screen-recorded one).
  // When present the popover renders it directly via <video>/<img>; iframe
  // and snapshot/action logic is bypassed entirely.
  media?: {
    kind: 'video' | 'image';
    mime?: string;
    // New path: server-hosted URL (e.g. /uploads/kb-clips/<id>/<hash>.webm).
    // Preferred for new clips. Read-side uses `mediaSrc()` to pick whichever
    // of `url` / `dataUrl` is present, so legacy articles keep working.
    url?: string;
    // Legacy: full base64 data URL inlined into the article JSON. Kept for
    // backwards compatibility — once every old article is re-saved we can
    // drop it. New uploads MUST NOT write this field.
    dataUrl?: string;
    filename?: string;
    size?: number;
    crop?: { x: number; y: number; w: number; h: number } | null;
    // Animated camera — array of keyframes ordered by `t` (seconds).
    // Player interpolates {x,y,w,h} between adjacent ones on each
    // video timeupdate. Wins over `crop` when set.
    //
    // Optional per-keyframe `hold` (seconds): keep the camera STATIC at
    // this keyframe for `hold` seconds before easing toward the next one.
    // Lets authors control "dwell time" without having to clone the
    // keyframe. Default 0 = immediate transition.
    cropKeyframes?: Array<{
      t: number;
      x: number;
      y: number;
      w: number;
      h: number;
      hold?: number;
    }> | null;
  } | null;
}

// Resolve a clip's media src: prefer the new server-hosted `url`, fall
// back to the legacy inline base64 `dataUrl` for articles saved before
// the multipart migration. Returns empty string when neither is present.
export function mediaSrc(media: KbClip['media'] | null | undefined): string {
  if (!media) return '';
  return media.url || media.dataUrl || '';
}

/**
 * Wire each clip up to its trigger in the article HTML.
 *
 * Two paths, in priority order:
 *   1. Anchor: clip has `anchorId` and the article HTML contains a
 *      `<span data-kb-clip-anchor="ID">…</span>`. We attach the trigger
 *      class + clip-idx to THAT exact span — no regex, no fuzzy match.
 *      Only the position the author picked lights up.
 *   2. Legacy fallback: clip has `term` only (older data). Wrap the FIRST
 *      occurrence of the term in the article — same regex behaviour as
 *      before. Once we transition the data, this branch fades out.
 */
export interface InjectClipState {
  /** Clip indices that have already been wrapped in earlier calls. */
  usedClipIdx: Set<number>;
  /**
   * For each clip's term (lowercased), how many occurrences we've already
   * SEEN across all calls — so the Nth-match logic spans the whole
   * article, not just one section.
   */
  termSeen: Map<string, number>;
}

export function injectClipMarkers(
  html: string,
  clips: KbClip[],
  state?: InjectClipState,
): string {
  if (!html || !clips || clips.length === 0) return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return html;

  // 1) Anchor-based wiring — find spans the editor inserted, attach the
  // hover handler. Skip clips with no anchorId (they fall through to the
  // regex fallback below).
  const anchorById = new Map<string, number>();
  clips.forEach((c, i) => {
    if (c.anchorId) anchorById.set(c.anchorId, i);
  });
  if (anchorById.size > 0) {
    const spans = root.querySelectorAll<HTMLElement>('span[data-kb-clip-anchor]');
    spans.forEach((span) => {
      const id = span.getAttribute('data-kb-clip-anchor');
      if (!id) return;
      const idx = anchorById.get(id);
      if (idx === undefined) return;
      span.setAttribute('data-kb-clip-idx', String(idx));
      span.classList.add('kb-clip-trigger');
    });
  }

  // 2) Term-based wiring for clips that don't already have an anchor span.
  //    For each clip we wrap exactly ONE occurrence of `term` — the Nth,
  //    where N = termOccurrence (default 0 = first match). Every other
  //    occurrence in the article stays plain text.
  const sortedClips = clips
    .map((c, i) => ({ ...c, _idx: i }))
    .filter((c) => !c.anchorId && c.term && c.term.trim().length > 0)
    .sort((a, b) => b.term.length - a.term.length);
  if (sortedClips.length === 0) {
    return root.innerHTML;
  }

  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Track which clips have been injected (so a single clip never wraps
  // twice) AND how many occurrences of each term we've already passed
  // (so termOccurrence counts strictly across the whole article, even
  // when the article is fed to us section-by-section).
  const usedClipIdx = state?.usedClipIdx ?? new Set<number>();
  const termSeen = state?.termSeen ?? new Map<string, number>();

  const walk = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (
        el.dataset?.kbClipIdx !== undefined ||
        el.tagName === 'CODE' ||
        el.tagName === 'PRE' ||
        el.tagName === 'A'
      ) {
        return;
      }
      const children = Array.from(el.childNodes);
      for (const ch of children) walk(ch);
      return;
    }
    if (node.nodeType !== Node.TEXT_NODE) return;
    const txt = node.nodeValue || '';
    if (!txt.trim()) return;
    let pieces: Array<{ kind: 'text'; v: string } | { kind: 'clip'; idx: number; v: string }> = [
      { kind: 'text', v: txt },
    ];

    for (const c of sortedClips) {
      if (usedClipIdx.has(c._idx)) continue;
      // Target occurrence index for this clip — what the picker saved.
      // No saved value = 0 (= first match), preserves old behaviour.
      const targetOcc = typeof c.termOccurrence === 'number' ? c.termOccurrence : 0;
      const termKey = c.term.toLowerCase();
      const next: typeof pieces = [];
      let alreadyMatchedThisClip = false;
      for (const p of pieces) {
        if (p.kind !== 'text' || alreadyMatchedThisClip) {
          next.push(p);
          continue;
        }
        const v = p.v;
        let lastIdx = 0;
        const re = new RegExp(`(${escape(c.term)})`, 'gi');
        let m: RegExpExecArray | null;
        const isWord = (ch: string) => /[\p{L}\p{N}_]/u.test(ch);
        while ((m = re.exec(v)) !== null) {
          const start = m.index;
          const end = start + m[1].length;
          const before = start > 0 ? v[start - 1] : '';
          const after = end < v.length ? v[end] : '';
          if (isWord(before) || isWord(after)) continue;
          const seenSoFar = termSeen.get(termKey) ?? 0;
          // Increment the global "we've seen one more" counter regardless
          // of whether THIS is the chosen occurrence.
          termSeen.set(termKey, seenSoFar + 1);
          if (seenSoFar !== targetOcc) {
            // Wrong occurrence — leave plain text in this slice but keep
            // scanning so termSeen advances correctly.
            continue;
          }
          if (start > lastIdx) next.push({ kind: 'text', v: v.slice(lastIdx, start) });
          next.push({ kind: 'clip', idx: c._idx, v: m[1] });
          lastIdx = end;
          alreadyMatchedThisClip = true;
          usedClipIdx.add(c._idx);
          // Don't break — let the regex finish so termSeen counts every
          // remaining occurrence in this text node too.
        }
        if (lastIdx < v.length) next.push({ kind: 'text', v: v.slice(lastIdx) });
      }
      pieces = next;
    }

    if (pieces.length === 1 && pieces[0].kind === 'text') return;

    const frag = doc.createDocumentFragment();
    for (const p of pieces) {
      if (p.kind === 'text') {
        frag.appendChild(doc.createTextNode(p.v));
      } else {
        const span = doc.createElement('span');
        span.setAttribute('data-kb-clip-idx', String(p.idx));
        span.className = 'kb-clip-trigger';
        span.textContent = p.v;
        frag.appendChild(span);
      }
    }
    node.parentNode?.replaceChild(frag, node);
  };
  walk(root);
  return root.innerHTML;
}

interface ClipPopupHostProps {
  clips: KbClip[];
}

interface HoverState {
  clipIdx: number;
  anchorRect: DOMRect;
}

/**
 * Pre-mounts one offscreen iframe per clip so the demo loads exactly ONCE per
 * page lifetime — hovers just reposition the wrapper, no re-fetch and no
 * scroll-jump from the demo app focusing inputs on (re)load. On hover or
 * click, the active clip's wrapper is moved into view and a synthetic cursor
 * loops over the recorded actions.
 */
export const ClipPopupHost: React.FC<ClipPopupHostProps> = ({ clips }) => {
  const [hover, setHover] = useState<HoverState | null>(null);
  // Раньше был режим «pinned» при клике, но юзер жаловался что попап
  // остаётся висеть после клика. Теперь чистый hover-mode: убрал курсор —
  // гифка исчезает. Click на слово больше не пинит, чтобы не было «застрял».
  const active = hover;

  useEffect(() => {
    if (!clips || clips.length === 0) return;
    const onOver = (e: Event) => {
      const t = (e.target as HTMLElement)?.closest?.('[data-kb-clip-idx]') as HTMLElement | null;
      if (!t) return;
      const idx = Number(t.getAttribute('data-kb-clip-idx'));
      if (Number.isNaN(idx) || idx < 0 || idx >= clips.length) return;
      const rect = t.getBoundingClientRect();
      setHover({ clipIdx: idx, anchorRect: rect });
    };
    const onOut = (e: Event) => {
      const related = (e as MouseEvent).relatedTarget as HTMLElement | null;
      // Куда курсор перешёл? Если в триггер или попап — НЕ закрываем.
      if (related) {
        if (related.closest?.('[data-kb-clip-popover]')) return;
        if (related.closest?.('[data-kb-clip-idx]')) return;
      }
      // Ловим mouseleave И от триггера И от попапа — иначе если юзер
      // прошёл по пути «слово → попап → пустота», mouseout последний раз
      // вышел из попапа, и старая проверка `e.target.closest(data-kb-clip-idx)`
      // его пропускала → popup оставался висеть.
      const target = e.target as HTMLElement;
      const fromTrigger = target?.closest?.('[data-kb-clip-idx]');
      const fromPopover = target?.closest?.('[data-kb-clip-popover]');
      if (!fromTrigger && !fromPopover) return;
      window.setTimeout(() => {
        // Финальный страж — если за 150ms курсор успел опять войти в
        // триггер/попап, не закрываем.
        if (document.querySelector('[data-kb-clip-popover]:hover')) return;
        if (document.querySelector('[data-kb-clip-idx]:hover')) return;
        setHover(null);
      }, 150);
    };
    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
    };
  }, [clips]);

  // Pre-render every clip's iframe in a stable wrapper. The non-active ones
  // are visibility:hidden + pointer-events:none + parked offscreen, so they
  // stay loaded but invisible.
  return (
    <>
      {clips.map((clip, i) => (
        <ClipFrame
          key={i}
          clip={clip}
          clipIdx={i}
          active={!!active && active.clipIdx === i}
          anchorRect={active && active.clipIdx === i ? active.anchorRect : null}
          pinned={false}
          onAutoPin={() => { /* pinned-режим отключён — hover-only */ }}
          onUnpin={() => { /* нет pinned-стейта */ }}
          onClose={() => setHover(null)}
        />
      ))}
    </>
  );
};

interface ClipFrameProps {
  clip: KbClip;
  clipIdx: number;
  active: boolean;
  anchorRect: DOMRect | null;
  pinned: boolean;
  onAutoPin: (idx: number, anchor: DOMRect) => void;
  onUnpin: () => void;
  onClose: () => void;
}

// =====================================================
// ClipMediaFrame — author uploaded a GIF / MP4 / WebM (or screen-recorded
// one). The popover just plays it on loop. Fixed window with reasonable
// max width, draggable header, no camera/cursor/iframe logic.
// =====================================================
const ClipMediaFrame: React.FC<ClipFrameProps> = ({ clip, clipIdx, active, anchorRect, pinned, onAutoPin, onUnpin, onClose }) => {
  const media = clip.media!;
  const videoRef = useRef<HTMLVideoElement>(null);
  const assetWrapperRef = useRef<HTMLDivElement>(null);
  // Real intrinsic dimensions discovered from the loaded asset, used to size
  // the popover so it preserves the file's aspect ratio.
  const [intrinsic, setIntrinsic] = useState<{ w: number; h: number } | null>(null);

  // User-pinned position on drag.
  const [manualPos, setManualPos] = useState<{ top: number; left: number } | null>(null);
  useEffect(() => {
    if (!active) setManualPos(null);
  }, [active]);

  // When popover hides, pause the video so we don't burn CPU off-screen.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) {
      v.currentTime = 0;
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [active]);

  // Robust intrinsic detection — onLoadedMetadata isn't always reliable for
  // off-screen <video> tags or large data URLs in some browsers (notably
  // Yandex). Poll videoWidth/videoHeight (or naturalWidth for images) every
  // 100ms until we get a valid value. Cancel on unmount.
  useEffect(() => {
    if (intrinsic) return;
    const id = setInterval(() => {
      const v = videoRef.current;
      if (!v) return;
      const w = (v as any).videoWidth || (v as any).naturalWidth || 0;
      const h = (v as any).videoHeight || (v as any).naturalHeight || 0;
      if (w > 0 && h > 0) {
        setIntrinsic({ w, h });
        clearInterval(id);
      }
    }, 100);
    return () => clearInterval(id);
  }, [intrinsic]);

  // Live crop — when cropKeyframes is set we interpolate it on every
  // video timeupdate so the popover camera animates over time. Otherwise
  // fall back to the static crop or no crop at all.
  //
  // Aspect lock: every keyframe is normalized so w_norm == h_norm — the
  // rect aspect (in pixels) always matches the video's intrinsic aspect.
  // Per-keyframe SIZE is preserved (different zoom levels are allowed),
  // only the RATIO is constrained. The popup window aspect = intrinsic
  // aspect, so cover-fit shows the rect 1:1 without cropping.
  //
  // useMemo is critical: without it the .map() returns a new array every
  // render, the rAF useEffect's [keyframes, active] deps tear down + run
  // again on each tick, the in-effect setLiveCrop(sorted[0]) hammers the
  // state back to KF1 forever, and the camera never moves. With a stable
  // memoized ref, the effect runs only when keyframes actually change.
  const keyframes = useMemo(() => {
    const raw = media.cropKeyframes;
    if (!raw || raw.length === 0) return null;
    return [...raw]
      .sort((a, b) => a.t - b.t)
      .map((kf) => {
        const size = kf.w === kf.h ? kf.w : Math.min(kf.w, kf.h);
        return { ...kf, w: size, h: size };
      });
  }, [media.cropKeyframes]);
  const firstKeyframe = keyframes ? keyframes[0] : null;
  const [liveCrop, setLiveCrop] = useState<{ x: number; y: number; w: number; h: number } | null>(
    firstKeyframe
      ? { x: firstKeyframe.x, y: firstKeyframe.y, w: firstKeyframe.w, h: firstKeyframe.h }
      : media.crop || null,
  );
  // The component is mounted for ALL clips on the article and visibility
  // just toggles via opacity. Between hovers, `liveCrop` retains the last
  // value from the prior animation (often the final keyframe). Without
  // this guard, the very first paint after activation shows that stale
  // value before the rAF tick can re-sync. Override at render-time on the
  // first render where `active` flips true: state catches up afterwards.
  const lastActiveRef = useRef(active);
  const justActivated = active && !lastActiveRef.current;
  useEffect(() => {
    lastActiveRef.current = active;
  });
  const crop = justActivated && firstKeyframe
    ? { x: firstKeyframe.x, y: firstKeyframe.y, w: firstKeyframe.w, h: firstKeyframe.h }
    : liveCrop;
  // Popup window aspect = the VIDEO's intrinsic aspect. With the
  // aspect-lock above (w_norm == h_norm in every keyframe), each rect's
  // pixel aspect equals (intrinsic.w * w) / (intrinsic.h * w) =
  // intrinsic.w / intrinsic.h — i.e. exactly the popup aspect. So every
  // keyframe maps 1:1 onto the popup without cropping or letterboxing,
  // regardless of which keyframe the camera is at. Pure pan + uniform
  // zoom inside a stable popup shape.
  const aspect = intrinsic ? intrinsic.w / intrinsic.h : 16 / 9;
  // ФИКСИРОВАННАЯ ширина popup'а — 480px для всех клипов, чтобы они
  // выглядели одинаково независимо от исходного видео-разрешения. Раньше
  // ширина зависела от intrinsic.w × minCropW — разные клипы получались
  // разного размера, юзер жаловался. Высота тянется по aspect от ширины.
  const POPUP_W = 480;
  const popupH = POPUP_W / aspect;
  // Cover-style fit: asset always fully covers the fixed popup window —
  // sides clipped if a later keyframe's aspect differs from the first.
  // The crop's CENTER is positioned at the popup's center so each
  // keyframe's "main subject" stays visible regardless of aspect mismatch.
  //
  // The wrapper has CONSTANT intrinsic dimensions and uses CSS `transform:
  // translate3d() scale()` for layout. transforms are GPU-composited and
  // preserve subpixel precision — without this, every per-frame change to
  // width/height/top/left would re-snap to integer pixels, producing the
  // 1px "shake" users complained about during zoom animation.
  let assetScale = 1;
  let assetLeft = 0;
  let assetTop = 0;
  if (intrinsic && crop && crop.w > 0 && crop.h > 0) {
    const cropPxW = intrinsic.w * crop.w;
    const cropPxH = intrinsic.h * crop.h;
    assetScale = Math.max(POPUP_W / cropPxW, popupH / cropPxH);
    const cropCenterX = (crop.x + crop.w / 2) * intrinsic.w * assetScale;
    const cropCenterY = (crop.y + crop.h / 2) * intrinsic.h * assetScale;
    assetLeft = POPUP_W / 2 - cropCenterX;
    assetTop = popupH / 2 - cropCenterY;
  } else if (intrinsic) {
    assetScale = POPUP_W / intrinsic.w;
  }

  // Diagnostic: log what's actually being applied when the popover opens —
  // helps surface "crop not actually saved" issues without DevTools digging.
  useEffect(() => {
    if (!active) return;
    // eslint-disable-next-line no-console
    console.log('[kb-clip] open', {
      term: clip.term,
      kind: media.kind,
      hasIntrinsic: !!intrinsic,
      intrinsic,
      crop,
      keyframeCount: keyframes?.length || 0,
      keyframes,
      computed: { POPUP_W, popupH, assetScale, assetLeft, assetTop, aspect },
    });
  }, [active]);

  // Animate crop along cropKeyframes — sample on every requestAnimationFrame.
  //
  // IMPORTANT: we drive the asset wrapper's `transform` IMPERATIVELY via a ref
  // instead of routing every frame through setState→React render.  Doing
  // 60 setState calls per second caused two visible problems:
  //   • re-render skew across rAFs translated into a sub-pixel "shake"
  //     between the wrapper and the surrounding popup chrome,
  //   • the React render loop was the bottleneck — zoom motion felt slow.
  // setLiveCrop is now only used for the one-shot initial paint when the
  // popup activates; after that we write style.transform directly.
  useEffect(() => {
    if (!keyframes || keyframes.length === 0) return;
    if (!active) return;
    if (!intrinsic) return;
    let raf = 0;
    const sorted = [...keyframes].sort((a, b) => a.t - b.t);
    // One-shot React state sync — guarantees the very first paint shows
    // keyframe[0] (otherwise carryover from a prior popup leaks through).
    setLiveCrop({ x: sorted[0].x, y: sorted[0].y, w: sorted[0].w, h: sorted[0].h });

    // Smooth, gentle ease — cubic in-out: symmetric acceleration and
    // deceleration, no abrupt snap.  Matches the "slow zoom" feel.
    const ease = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const POPUP_W_LOCAL = POPUP_W;
    const POPUP_H_LOCAL = popupH;
    const intrinsicW = intrinsic.w;
    const intrinsicH = intrinsic.h;
    const applyTransform = (cx: number, cy: number, cw: number, ch: number) => {
      const node = assetWrapperRef.current;
      if (!node) return;
      const cropPxW = intrinsicW * cw;
      const cropPxH = intrinsicH * ch;
      if (cropPxW <= 0 || cropPxH <= 0) return;
      const scale = Math.max(POPUP_W_LOCAL / cropPxW, POPUP_H_LOCAL / cropPxH);
      const centerX = (cx + cw / 2) * intrinsicW * scale;
      const centerY = (cy + ch / 2) * intrinsicH * scale;
      const left = POPUP_W_LOCAL / 2 - centerX;
      const top = POPUP_H_LOCAL / 2 - centerY;
      // translate3d → GPU layer, scale → GPU.  No DOM reflow per frame.
      node.style.transform = `translate3d(${left}px, ${top}px, 0) scale(${scale})`;
    };

    const tick = () => {
      const v = videoRef.current;
      if (v) {
        const t = v.currentTime;
        let activeIdx = -1;
        for (let i = sorted.length - 1; i >= 0; i--) {
          if (sorted[i].t <= t) { activeIdx = i; break; }
        }
        if (activeIdx === -1) {
          const k = sorted[0];
          applyTransform(k.x, k.y, k.w, k.h);
        } else {
          const activeKf = sorted[activeIdx];
          const next = activeIdx < sorted.length - 1 ? sorted[activeIdx + 1] : null;
          const holdDur = Math.max(0, activeKf.hold ?? 0);
          const holdEnd = activeKf.t + holdDur;
          // Зум стартует за LEAD секунд ДО следующего ключа и завершается
          // ровно в момент ключа. До окна перехода держим текущий кадр.
          // Так «ключ срабатывает за секунду до его времени» и приходит
          // точно на keyframe. Если расстояние до ключа меньше LEAD —
          // переход просто занимает весь доступный остаток.
          const LEAD = 2.0;
          if (!next) {
            applyTransform(activeKf.x, activeKf.y, activeKf.w, activeKf.h);
          } else {
            const transStart = Math.max(holdEnd, next.t - LEAD);
            if (t <= transStart) {
              applyTransform(activeKf.x, activeKf.y, activeKf.w, activeKf.h);
            } else {
              const transitionDur = Math.max(0.001, next.t - transStart);
              const ratio = Math.max(0, Math.min(1, (t - transStart) / transitionDur));
              const e = ease(ratio);
              applyTransform(
                activeKf.x + (next.x - activeKf.x) * e,
                activeKf.y + (next.y - activeKf.y) * e,
                activeKf.w + (next.w - activeKf.w) * e,
                activeKf.h + (next.h - activeKf.h) * e,
              );
            }
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [keyframes, active, intrinsic, popupH]);

  const margin = 12;
  const popoverStyle: React.CSSProperties = {
    position: 'fixed',
    width: POPUP_W,
    height: popupH,
    zIndex: 9999,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
    background: '#000',
    pointerEvents: active ? 'auto' : 'none',
    visibility: active ? 'visible' : 'hidden',
    opacity: active ? 1 : 0,
    transition: manualPos ? 'none' : 'opacity 0.12s ease',
  };
  if (active && manualPos) {
    popoverStyle.top = manualPos.top;
    popoverStyle.left = manualPos.left;
  } else if (active && anchorRect) {
    // popupH is stable now (locked to first keyframe), so plain top/left
    // positioning is fine — no flicker.
    const above = anchorRect.top > popupH + margin + 24;
    const top = above ? anchorRect.top - popupH - margin : anchorRect.bottom + margin;
    let left = anchorRect.left + anchorRect.width / 2 - POPUP_W / 2;
    left = Math.max(8, Math.min(window.innerWidth - POPUP_W - 8, left));
    popoverStyle.top = top;
    popoverStyle.left = left;
  } else {
    popoverStyle.top = -10000;
    popoverStyle.left = -10000;
  }

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (anchorRect) onAutoPin(clipIdx, anchorRect);
    const startTop = (popoverStyle.top as number) || 0;
    const startLeft = (popoverStyle.left as number) || 0;
    const startX = e.clientX;
    const startY = e.clientY;
    const onMove = (ev: MouseEvent) => {
      const nextLeft = startLeft + (ev.clientX - startX);
      const nextTop = startTop + (ev.clientY - startY);
      setManualPos({
        top: Math.max(8, Math.min(window.innerHeight - popupH - 8, nextTop)),
        left: Math.max(8, Math.min(window.innerWidth - POPUP_W - 8, nextLeft)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div data-kb-clip-popover style={popoverStyle}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          background: '#000',
        }}
      >
        {/* Asset wrapped in a fixed-size <div> at intrinsic dimensions and
            positioned via CSS transform. translate3d + scale go through
            GPU compositing with subpixel precision — without it, every
            frame's `top/left/width/height` change re-snapped to integer
            pixels and the camera trembled visibly during zoom. Hidden via
            opacity until intrinsic is known. */}
        <div
          ref={assetWrapperRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            // Until intrinsic dims are known, render at the popup size with
            // object-fit: contain on the media — that way the user sees the
            // first frame immediately instead of a black panel if the browser
            // (notably Yandex) fails to report videoWidth/Height via the
            // event AND the 100ms poll. Once intrinsic loads we swap to the
            // exact dimensions + crop-aware transform.
            width: intrinsic ? `${intrinsic.w}px` : `${POPUP_W}px`,
            height: intrinsic ? `${intrinsic.h}px` : `${popupH}px`,
            transform: intrinsic
              ? `translate3d(${assetLeft}px, ${assetTop}px, 0) scale(${assetScale})`
              : 'none',
            transformOrigin: '0 0',
            willChange: 'transform',
            opacity: 1,
            transition: 'opacity 0.12s ease',
            pointerEvents: 'none',
            // backface + perspective stabilise sub-pixel positioning when the
            // GPU compositor places the textured layer — eliminates the
            // residual 1px shimmer during fast pan/zoom transitions.
            backfaceVisibility: 'hidden',
            perspective: 1000,
            // Subtle contrast/saturation boost — counters the slight
            // softening that bilinear downsampling adds, without making
            // the image look unnatural.
            filter: 'contrast(1.06) saturate(1.05)',
          }}
        >
          {media.kind === 'video' ? (
            <video
              ref={videoRef}
              src={mediaSrc(media)}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                if (v.videoWidth > 0 && v.videoHeight > 0) {
                  setIntrinsic({ w: v.videoWidth, h: v.videoHeight });
                }
              }}
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: intrinsic ? 'fill' : 'contain',
                pointerEvents: 'none',
              }}
            />
          ) : (
            <img
              src={mediaSrc(media)}
              alt={`Превью: ${clip.term}`}
              draggable={false}
              onLoad={(e) => {
                const img = e.currentTarget;
                if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                  setIntrinsic({ w: img.naturalWidth, h: img.naturalHeight });
                }
              }}
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: intrinsic ? 'fill' : 'contain',
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      </div>

      {/* Drag handle / term label (same styling as ClipDemoFrame). */}
      <div
        onMouseDown={startDrag}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 26,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 8px',
          background: 'linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.0))',
          cursor: manualPos ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        title="Перетащи окно куда удобно"
      >
        <span
          style={{
            padding: '2px 8px',
            fontSize: 11,
            fontWeight: 700,
            color: '#1a1a1a',
            background: '#fafafa',
            borderRadius: 999,
          }}
        >
          {clip.term}
        </span>
        <span style={{ flex: 1 }} />
        <span aria-hidden style={{ fontSize: 14, lineHeight: 1, opacity: 0.55, color: '#fff', letterSpacing: 1 }}>
          ⋮⋮
        </span>
        <button
          type="button"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (pinned) {
              onUnpin();
            } else if (anchorRect) {
              onAutoPin(clipIdx, anchorRect);
            }
          }}
          title={pinned ? 'Открепить' : 'Закрепить превью на месте'}
          style={{
            marginLeft: 4,
            width: 22,
            height: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            borderRadius: 4,
            background: pinned ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.12)',
            color: '#fff',
            fontSize: 12,
            lineHeight: 1,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          📌
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClose();
          }}
          title="Закрыть"
          style={{
            marginLeft: 4,
            width: 22,
            height: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            borderRadius: 4,
            background: 'rgba(255,255,255,0.12)',
            color: '#fff',
            fontSize: 14,
            lineHeight: 1,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

const ClipFrame: React.FC<ClipFrameProps> = (props) => {
  // Dispatcher — keeps the hook order stable. The author's choice between
  // a simple uploaded media file and the advanced demo-iframe path is made
  // here at the top.
  if (props.clip.media) return <ClipMediaFrame {...props} />;
  return <ClipDemoFrame {...props} />;
};

const ClipDemoFrame: React.FC<ClipFrameProps> = ({ clip, clipIdx, active, anchorRect, pinned, onAutoPin, onUnpin, onClose }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Snapshot mode: when the clip has pre-recorded screenshots we never load
  // the live demo. The player just swaps between snapshots based on which
  // click/type action the chain has reached.
  const hasSnapshots = Array.isArray(clip.snapshots) && clip.snapshots.length > 0;
  const [snapshotIdx, setSnapshotIdx] = useState(0);
  // The popover's "camera" — animates the X/Y pan between viewboxes encoded
  // in clip.actions during playback. The W/H of the camera (i.e. the zoom
  // level) is locked to the *initial* clip.box so subsequent viewbox actions
  // only move the camera, they don't change the zoom.
  const [viewBox, setViewBox] = useState<[number, number, number, number]>(clip.box);
  const [viewTransitionMs, setViewTransitionMs] = useState(0);
  useEffect(() => {
    if (!active) {
      setViewBox(clip.box);
      setViewTransitionMs(0);
      setSnapshotIdx(0);
      // Reset the iframe (only if we're using live mode — snapshot mode
      // doesn't load it at all).
      if (!hasSnapshots) {
        const iframe = iframeRef.current;
        if (iframe) {
          try {
            const cur = iframe.contentWindow?.location;
            const target = clip.iframeUrl;
            if (cur && (cur.pathname + cur.search + cur.hash) !== target) {
              iframe.contentWindow?.location?.replace(target);
            }
          } catch {}
        }
      }
    }
  }, [active, clip.box, clip.iframeUrl, hasSnapshots]);

  // Popover *window* size is fixed for the lifetime of the popup — it's
  // derived from the initial clip.box. The iframe inside is then scaled
  // (transform: scale) to fit the *current* viewBox into that fixed window.
  //   smaller viewBox.W → larger iframe scale → "zoom in" feel
  //   larger viewBox.W → smaller iframe scale → "zoom out" feel
  // Animating the scale via CSS transition gives a smooth dolly between
  // recorded frames.
  const MAX_W = 600;
  const MIN_W = 240;
  const POPUP_W = Math.min(MAX_W, Math.max(MIN_W, clip.box[2] * 0.7));
  const popupH = clip.box[3] * (POPUP_W / clip.box[2]);
  // Live scale = popup window width / current view box width. With viewBox
  // = clip.box (initial), this matches the original "static" scale.
  const scale = POPUP_W / viewBox[2];

  // User-pinned position when they drag the popover by its header. Resets
  // when the popover closes, so each new hover starts auto-positioned.
  const [manualPos, setManualPos] = useState<{ top: number; left: number } | null>(null);
  useEffect(() => {
    if (!active) setManualPos(null);
  }, [active]);

  // Position above anchor when there's room, otherwise below. When inactive,
  // park offscreen so the page never feels its presence.
  const margin = 12;
  let popoverStyle: React.CSSProperties = {
    position: 'fixed',
    width: POPUP_W,
    height: popupH,
    zIndex: 9999,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
    background: '#1a1a1a',
    pointerEvents: active ? 'auto' : 'none',
    visibility: active ? 'visible' : 'hidden',
    opacity: active ? 1 : 0,
    transition: manualPos ? 'none' : 'opacity 0.12s ease',
  };
  if (active && manualPos) {
    popoverStyle.top = manualPos.top;
    popoverStyle.left = manualPos.left;
  } else if (active && anchorRect) {
    const above = anchorRect.top > popupH + margin + 24;
    const top = above ? anchorRect.top - popupH - margin : anchorRect.bottom + margin;
    let left = anchorRect.left + anchorRect.width / 2 - POPUP_W / 2;
    left = Math.max(8, Math.min(window.innerWidth - POPUP_W - 8, left));
    popoverStyle.top = top;
    popoverStyle.left = left;
  } else {
    // Park offscreen but kept mounted so iframe stays warm.
    popoverStyle.top = -10000;
    popoverStyle.left = -10000;
  }

  // Drag handler — used on the header strip and on the optional resize area.
  // Captures the popover's current screen position from the live style and
  // updates manualPos as the mouse moves.
  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Pin this clip so it stays open even when the cursor leaves the term
    // and the popover during drag.
    if (anchorRect) onAutoPin(clipIdx, anchorRect);
    const startTop = (popoverStyle.top as number) || 0;
    const startLeft = (popoverStyle.left as number) || 0;
    const startX = e.clientX;
    const startY = e.clientY;
    const onMove = (ev: MouseEvent) => {
      const nextLeft = startLeft + (ev.clientX - startX);
      const nextTop = startTop + (ev.clientY - startY);
      setManualPos({
        top: Math.max(8, Math.min(window.innerHeight - popupH - 8, nextTop)),
        left: Math.max(8, Math.min(window.innerWidth - POPUP_W - 8, nextLeft)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // No width/height animation — zoom stays locked, only the iframe inside
  // pans between viewboxes.
  popoverStyle.transition = manualPos ? 'none' : 'opacity 0.12s ease';

  return (
    <div data-kb-clip-popover style={popoverStyle}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          width: POPUP_W,
          height: popupH,
          overflow: 'hidden',
        }}
      >
        {hasSnapshots ? (
          <ClipSnapshot
            clip={clip}
            snapshotIdx={snapshotIdx}
            viewBox={viewBox}
            scale={scale}
            transitionMs={viewTransitionMs}
          />
        ) : (
          <ClipIframe
            ref={iframeRef}
            clip={clip}
            viewBox={viewBox}
            scale={scale}
            transitionMs={viewTransitionMs}
          />
        )}
        {/* Action playback overlay. In snapshot mode we don't dispatch real
            clicks into the iframe — we just animate the cursor and bump
            snapshotIdx after each click/type so the picture advances. */}
        {active && Array.isArray(clip.actions) && clip.actions.length > 0 && (
          <ClipActionPlayer
            clip={clip}
            iframeRef={iframeRef}
            viewBox={viewBox}
            scale={scale}
            active={active}
            useSnapshots={hasSnapshots}
            onSnapshotChange={setSnapshotIdx}
            onViewBox={(box, dur) => {
              setViewTransitionMs(dur);
              setViewBox(box);
            }}
          />
        )}
      </div>
      {/* Drag handle — top strip across the whole popover. The visible
          purple "term" label sits inside the strip so the user has a clear
          place to grab. */}
      <div
        onMouseDown={startDrag}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 26,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 8px',
          background: 'linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.0))',
          cursor: manualPos ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        title="Перетащи окно куда удобно"
      >
        <span
          style={{
            padding: '2px 8px',
            fontSize: 11,
            fontWeight: 700,
            color: '#1a1a1a',
            background: '#fafafa',
            borderRadius: 999,
          }}
        >
          {clip.term}
        </span>
        <span style={{ flex: 1 }} />
        {/* tiny "drag me" hint */}
        <span
          aria-hidden
          style={{
            fontSize: 14,
            lineHeight: 1,
            opacity: 0.55,
            color: '#fff',
            letterSpacing: 1,
          }}
        >
          ⋮⋮
        </span>
        <button
          type="button"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (pinned) {
              onUnpin();
            } else if (anchorRect) {
              onAutoPin(clipIdx, anchorRect);
            }
          }}
          title={pinned ? 'Открепить' : 'Закрепить превью на месте'}
          style={{
            marginLeft: 4,
            width: 22,
            height: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            borderRadius: 4,
            background: pinned ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.12)',
            color: '#fff',
            fontSize: 12,
            lineHeight: 1,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          📌
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClose();
          }}
          title="Закрыть"
          style={{
            marginLeft: 4,
            width: 22,
            height: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            borderRadius: 4,
            background: 'rgba(255,255,255,0.12)',
            color: '#fff',
            fontSize: 14,
            lineHeight: 1,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

// Renders a recorded snapshot (base64 JPEG data URL) cropped + scaled to fit
// the popover window. Same crop math as ClipIframe so cursor overlays and
// viewBox transitions map identically. The image is rendered at NATURAL
// 1920×1080 px and CSS-scaled, mirroring the iframe layout.
const ClipSnapshot: React.FC<{
  clip: KbClip;
  snapshotIdx: number;
  viewBox: [number, number, number, number];
  scale: number;
  transitionMs: number;
}> = ({ clip, snapshotIdx, viewBox, scale, transitionMs }) => {
  const src = (clip.snapshots && clip.snapshots[snapshotIdx]) || clip.snapshots?.[0] || '';
  return (
    <img
      src={src}
      alt={`Превью: ${clip.term}`}
      draggable={false}
      style={{
        position: 'absolute',
        top: -viewBox[1] * scale,
        left: -viewBox[0] * scale,
        width: `${DEMO_W}px`,
        height: `${DEMO_H}px`,
        transform: `scale(${scale})`,
        transformOrigin: '0 0',
        objectFit: 'cover',
        background: '#fff',
        userSelect: 'none',
        pointerEvents: 'none',
        transition:
          transitionMs > 0
            ? `top ${transitionMs}ms cubic-bezier(0.45, 0, 0.55, 1), left ${transitionMs}ms cubic-bezier(0.45, 0, 0.55, 1), transform ${transitionMs}ms cubic-bezier(0.45, 0, 0.55, 1)`
            : 'none',
      }}
    />
  );
};

const ClipIframe = React.forwardRef<HTMLIFrameElement, {
  clip: KbClip;
  viewBox: [number, number, number, number];
  scale: number;
  transitionMs: number;
}>(({ clip, viewBox, scale, transitionMs }, fwdRef) => {
  const innerRef = useRef<HTMLIFrameElement>(null);
  React.useImperativeHandle(fwdRef, () => innerRef.current as HTMLIFrameElement);
  // Save+restore the parent window scroll across the iframe's first load,
  // because some demo screens auto-focus an input on mount and that focus
  // can scroll the iframe element into view in the parent.
  const handleLoad = () => {
    const sx = window.scrollX;
    const sy = window.scrollY;
    requestAnimationFrame(() => {
      if (window.scrollX !== sx || window.scrollY !== sy) {
        window.scrollTo(sx, sy);
      }
      setTimeout(() => {
        if (window.scrollX !== sx || window.scrollY !== sy) {
          window.scrollTo(sx, sy);
        }
      }, 80);
    });
    try {
      const doc = innerRef.current?.contentDocument;
      const ae = doc?.activeElement as HTMLElement | null;
      if (ae && typeof ae.blur === 'function') ae.blur();
    } catch {}
  };
  // Render the iframe at NATURAL 1920×1080 size and CSS-transform: scale it
  // down. That way the demo's responsive layout doesn't reflow at the smaller
  // popover size, and recorded coords (which are in 1920×1080 virtual px)
  // line up 1:1 with the iframe's internal viewport — so synthetic clicks
  // hit the right element.
  return (
    <iframe
      ref={innerRef}
      src={clip.iframeUrl}
      title={`Превью: ${clip.term}`}
      scrolling="no"
      tabIndex={-1}
      aria-hidden="true"
      onLoad={handleLoad}
      style={{
        position: 'absolute',
        top: -viewBox[1] * scale,
        left: -viewBox[0] * scale,
        width: `${DEMO_W}px`,
        height: `${DEMO_H}px`,
        transform: `scale(${scale})`,
        transformOrigin: '0 0',
        border: 0,
        background: '#fff',
        pointerEvents: 'none',
        transition:
          transitionMs > 0
            ? `top ${transitionMs}ms cubic-bezier(0.45, 0, 0.55, 1), left ${transitionMs}ms cubic-bezier(0.45, 0, 0.55, 1), transform ${transitionMs}ms cubic-bezier(0.45, 0, 0.55, 1)`
            : 'none',
      }}
    />
  );
});
ClipIframe.displayName = 'ClipIframe';

// =====================================================
// Action player — animated cursor + ripples + highlights overlaid on the
// cropped iframe. Dispatches REAL click/type events into the iframe so the
// demo actually advances through the recorded steps. After the loop ends
// the iframe is reloaded back to the start URL for a clean replay.
// =====================================================
interface PlayerCursor {
  x: number;
  y: number;
  clicking: boolean;
  transitionMs: number;
}
interface ActionPlayerProps {
  clip: KbClip;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  viewBox: [number, number, number, number];
  scale: number;
  active: boolean;
  // In snapshot mode the player doesn't touch the iframe — it just animates
  // the cursor and tells the parent which snapshot index to display.
  useSnapshots?: boolean;
  onSnapshotChange?: (idx: number) => void;
  onViewBox: (box: [number, number, number, number], durationMs: number) => void;
}

// Set value on an <input>/<textarea> in a way React's controlled inputs notice.
// We use the prototype setter so the change event reaches React's value tracker.
function setNativeInputValue(el: HTMLElement, value: string) {
  try {
    const proto =
      el.tagName === 'TEXTAREA'
        ? (el.ownerDocument?.defaultView as any)?.HTMLTextAreaElement?.prototype
        : (el.ownerDocument?.defaultView as any)?.HTMLInputElement?.prototype;
    const setter = proto && Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (setter) setter.call(el, value);
    else (el as HTMLInputElement).value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  } catch {}
}

// Fire a real-looking pointer + mouse + click sequence into a same-origin
// iframe at the given coordinates. cx/cy are in the iframe's natural
// 1920×1080 viewport (= our virtual coord system).
function dispatchClickIntoFrame(iframe: HTMLIFrameElement | null, cx: number, cy: number) {
  if (!iframe) return;
  try {
    const ifw = iframe.contentWindow as any;
    const doc = iframe.contentDocument;
    if (!doc || !ifw) return;
    const points: Array<[number, number]> = [
      [cx, cy], [cx - 2, cy - 2], [cx + 2, cy + 2],
      [cx, cy - 4], [cx, cy + 4], [cx - 4, cy], [cx + 4, cy],
    ];
    const seen = new Set<Element>();
    const candidates: Element[] = [];
    for (const [x, y] of points) {
      const el = doc.elementFromPoint(x, y);
      if (el && !seen.has(el)) { seen.add(el); candidates.push(el); }
    }
    if (!candidates.length) return;
    const target =
      candidates.find((el) => el.closest('button, a, [role="button"], [onclick], input, [data-clickable]'))
        ?.closest('button, a, [role="button"], [onclick], input, [data-clickable]') || candidates[0];
    const opts: any = {
      view: ifw, bubbles: true, cancelable: true, composed: true,
      clientX: cx, clientY: cy, screenX: cx, screenY: cy, button: 0, buttons: 1,
    };
    if (typeof ifw.PointerEvent === 'function') {
      const pOpts = { ...opts, pointerType: 'mouse', pointerId: 1, isPrimary: true };
      target.dispatchEvent(new ifw.PointerEvent('pointerover', pOpts));
      target.dispatchEvent(new ifw.PointerEvent('pointerdown', pOpts));
      target.dispatchEvent(new ifw.PointerEvent('pointerup', { ...pOpts, buttons: 0 }));
    }
    target.dispatchEvent(new ifw.MouseEvent('mousedown', opts));
    target.dispatchEvent(new ifw.MouseEvent('mouseup', { ...opts, buttons: 0 }));
    target.dispatchEvent(new ifw.MouseEvent('click', { ...opts, buttons: 0 }));
  } catch (e) {
    // Cross-origin or transient — ignore.
  }
}
const DEFAULT_MOVE_MS = 900;
const CLICK_PAUSE_MS = 450;
const COMMENT_DEFAULT_MS = 2200;
const HIGHLIGHT_DEFAULT_MS = 2000;
const LOOP_GAP_MS = 700;

const ClipActionPlayer: React.FC<ActionPlayerProps> = ({
  clip,
  iframeRef,
  viewBox,
  scale,
  active,
  useSnapshots,
  onSnapshotChange,
  onViewBox,
}) => {
  const [cursor, setCursor] = useState<PlayerCursor>({
    x: viewBox[0] + viewBox[2] / 2,
    y: viewBox[1] + viewBox[3] / 2,
    clicking: false,
    transitionMs: 0,
  });
  const [overlayComment, setOverlayComment] = useState<{ x: number; y: number; text: string } | null>(null);
  const [overlayHighlight, setOverlayHighlight] = useState<[number, number, number, number] | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stop any pending timers when stopping playback.
  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (!active) {
      clearTimer();
      return;
    }
    let cancelled = false;
    let i = 0;
    const actions = clip.actions || [];
    if (actions.length === 0) return;

    const runStep = () => {
      if (cancelled) return;
      if (i >= actions.length) {
        // Loop end — small gap, reset to start frame, then restart.
        i = 0;
        setOverlayComment(null);
        setOverlayHighlight(null);
        onViewBox(clip.box, 400);
        if (useSnapshots) {
          // Snapshot mode: just rewind to frame 0, no iframe reload needed.
          if (onSnapshotChange) onSnapshotChange(0);
          timerRef.current = setTimeout(runStep, LOOP_GAP_MS);
          return;
        }
        const iframe = iframeRef.current;
        if (iframe) {
          let resumed = false;
          const onLoad = () => {
            if (resumed) return;
            resumed = true;
            iframe.removeEventListener('load', onLoad);
            timerRef.current = setTimeout(runStep, 250);
          };
          iframe.addEventListener('load', onLoad);
          try {
            // Use replace so the parent's history doesn't accumulate entries.
            iframe.contentWindow?.location?.replace(clip.iframeUrl);
          } catch {
            // Cross-origin or transient — fall back to restarting without reload.
            iframe.removeEventListener('load', onLoad);
            timerRef.current = setTimeout(runStep, LOOP_GAP_MS);
          }
          // Safety: if the load event never fires (e.g. URL didn't change),
          // resume after a generous timeout.
          setTimeout(() => {
            if (!resumed) {
              resumed = true;
              iframe.removeEventListener('load', onLoad);
              timerRef.current = setTimeout(runStep, 100);
            }
          }, 2500);
        } else {
          timerRef.current = setTimeout(runStep, LOOP_GAP_MS);
        }
        return;
      }
      const a = actions[i++];
      if (!a || typeof a !== 'object' || !a.type) {
        timerRef.current = setTimeout(runStep, 30);
        return;
      }
      switch (a.type) {
        case 'move': {
          const [x, y] = a.to || [0, 0];
          const dur = a.duration ?? DEFAULT_MOVE_MS;
          setCursor((c) => ({ ...c, x, y, clicking: false, transitionMs: dur }));
          timerRef.current = setTimeout(runStep, dur + 20);
          break;
        }
        case 'click': {
          const [x, y] = a.at || [0, 0];
          setCursor((c) => ({ ...c, x, y, clicking: true, transitionMs: 350 }));
          timerRef.current = setTimeout(() => {
            if (useSnapshots) {
              // Snapshot mode: jump to the snapshot that was captured AFTER
              // this click during recording.
              if (typeof a.snapshotIdx === 'number' && onSnapshotChange) {
                onSnapshotChange(a.snapshotIdx);
              }
            } else {
              // Live mode: dispatch the real click into the iframe.
              dispatchClickIntoFrame(iframeRef.current, x, y);
            }
            setCursor((c) => ({ ...c, clicking: false }));
            timerRef.current = setTimeout(runStep, CLICK_PAUSE_MS);
          }, 380);
          break;
        }
        case 'wait':
          timerRef.current = setTimeout(runStep, a.duration || 600);
          break;
        case 'type': {
          const [x, y] = a.at || [0, 0];
          const text = String(a.text || '');
          // 1) Move cursor to the input.
          setCursor((c) => ({ ...c, x, y, clicking: false, transitionMs: 350 }));
          timerRef.current = setTimeout(() => {
            // Snapshot mode — jump to the captured "after typing" frame and
            // skip live input mutation entirely. We still pause to mimic
            // typing time so the gif feels right.
            if (useSnapshots) {
              if (typeof a.snapshotIdx === 'number' && onSnapshotChange) {
                onSnapshotChange(a.snapshotIdx);
              }
              const fakeDur = Math.min(2400, Math.max(600, text.length * 70));
              timerRef.current = setTimeout(runStep, fakeDur);
              return;
            }
            // 2) Find the live input via elementFromPoint at the action coords.
            const iframe = iframeRef.current;
            const doc = iframe?.contentDocument;
            let inputEl: HTMLElement | null = null;
            try {
              if (doc) {
                const hit = doc.elementFromPoint(x, y) as HTMLElement | null;
                if (hit) {
                  inputEl =
                    hit.closest<HTMLElement>('input, textarea, [contenteditable]') || hit;
                }
              }
            } catch {}
            // Click the input first so it's focused (some demos render
            // differently when focused — autocomplete, etc.).
            if (inputEl) {
              try {
                inputEl.focus();
              } catch {}
              dispatchClickIntoFrame(iframe, x, y);
            }
            if (!inputEl || !text) {
              // No input found — just pause as visual hint and advance.
              timerRef.current = setTimeout(runStep, 600);
              return;
            }
            // 3) Type characters one-by-one. Total duration scales with
            // length but capped so long strings don't stretch the loop.
            const PER_CHAR_MIN = 35;
            const PER_CHAR_MAX = 90;
            const TOTAL_CAP = 2400;
            const perChar = Math.max(
              PER_CHAR_MIN,
              Math.min(PER_CHAR_MAX, Math.round(TOTAL_CAP / Math.max(1, text.length))),
            );
            // Start from empty value so the typing is visible even if the
            // demo retained a previous value from earlier in the loop.
            try {
              setNativeInputValue(inputEl, '');
            } catch {}
            let i = 0;
            const typeNext = () => {
              if (i > text.length) {
                timerRef.current = setTimeout(runStep, 250);
                return;
              }
              const partial = text.slice(0, i);
              try {
                setNativeInputValue(inputEl as HTMLElement, partial);
              } catch {}
              i++;
              timerRef.current = setTimeout(typeNext, perChar);
            };
            typeNext();
          }, 380);
          break;
        }
        case 'comment': {
          const [x, y] = a.at || [0, 0];
          const text = String(a.text || '');
          const dur = a.duration ?? COMMENT_DEFAULT_MS;
          setOverlayComment({ x, y, text });
          timerRef.current = setTimeout(() => {
            setOverlayComment(null);
            runStep();
          }, dur);
          break;
        }
        case 'highlight': {
          const dur = a.duration ?? HIGHLIGHT_DEFAULT_MS;
          setOverlayHighlight(a.box || null);
          timerRef.current = setTimeout(() => {
            setOverlayHighlight(null);
            runStep();
          }, dur);
          break;
        }
        case 'hotspot': {
          // Visualize as a brief highlight (no input expected in preview).
          setOverlayHighlight(a.box || null);
          timerRef.current = setTimeout(() => {
            setOverlayHighlight(null);
            runStep();
          }, 1400);
          break;
        }
        case 'viewbox': {
          // Pan the popover's "camera" to a new region of the demo.
          const dur = a.duration ?? 700;
          if (Array.isArray(a.box) && a.box.length === 4) {
            onViewBox(a.box as [number, number, number, number], dur);
          }
          timerRef.current = setTimeout(runStep, dur + 80);
          break;
        }
        default:
          timerRef.current = setTimeout(runStep, 200);
      }
    };

    timerRef.current = setTimeout(runStep, 250);
    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [active, clip]);

  // Translate document-space coords into popover-local pixels using the
  // *current* view box (the one the popover camera is panned to). This way
  // overlays stay anchored to the demo even as the view moves.
  const localX = (x: number) => (x - viewBox[0]) * scale;
  const localY = (y: number) => (y - viewBox[1]) * scale;

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {overlayHighlight && (
        <div
          style={{
            position: 'absolute',
            left: localX(overlayHighlight[0]),
            top: localY(overlayHighlight[1]),
            width: overlayHighlight[2] * scale,
            height: overlayHighlight[3] * scale,
            border: '3px solid #a78bfa',
            background: 'rgba(167,139,250,0.14)',
            borderRadius: 4,
            boxShadow: '0 0 0 4px rgba(167,139,250,0.25), 0 0 20px rgba(167,139,250,0.55)',
            animation: 'kb-clip-pulse 1.4s ease-in-out infinite',
          }}
        />
      )}
      {overlayComment && (
        <div
          style={{
            position: 'absolute',
            left: localX(overlayComment.x),
            top: localY(overlayComment.y),
            transform: 'translate(-8px, -100%)',
            background: 'rgba(255,215,0,0.96)',
            color: '#1a1a1a',
            padding: '4px 8px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 500,
            maxWidth: 220,
            boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
            border: '1.5px solid #b8860b',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.3,
          }}
        >
          {overlayComment.text}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: localX(cursor.x),
          top: localY(cursor.y),
          transform: 'translate(-2px, -1px)',
          transition: `left ${cursor.transitionMs}ms cubic-bezier(0.25,0.1,0.25,1), top ${cursor.transitionMs}ms cubic-bezier(0.25,0.1,0.25,1)`,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.7))' }}>
          <path d="M5 3L19 12L12 13L9 20L5 3Z" fill="white" stroke="black" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
        {cursor.clicking && (
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 36,
              height: 36,
              transform: 'translate(-18px, -18px)',
              borderRadius: '50%',
              background: 'rgba(255,82,82,0.3)',
              border: '2px solid #ff5252',
              animation: 'kb-clip-ripple 0.5s ease-out',
            }}
          />
        )}
      </div>
      <style>{`
        @keyframes kb-clip-ripple {
          0% { transform: translate(-18px,-18px) scale(0.2); opacity: 1; }
          100% { transform: translate(-18px,-18px) scale(1.4); opacity: 0; }
        }
        @keyframes kb-clip-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(167,139,250,0.25), 0 0 20px rgba(167,139,250,0.55); }
          50% { box-shadow: 0 0 0 6px rgba(167,139,250,0.35), 0 0 28px rgba(167,139,250,0.8); }
        }
      `}</style>
    </div>
  );
};
